import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import ActivateTabletModeModal from '~/components/modals/activateTabletModeModal.vue'

export const useAuthStore = defineStore('auth', () => {
  const nuxtApp = useNuxtApp()
  const { $i18n, $api } = useNuxtApp()

  const authenticated = ref(false)
  const loading = ref(false)
  const user = ref<User | null>(null)
  const notifications = ref<Notifications>([])
  const showSidebar = ref(true)
  const didSaveNotifications = ref(false)
  const ongoingGame = ref<Game | null>(null)
  const isPrinterConnected = ref(false)

  const tabletMode = useLocalStorage<boolean>(`tabletMode`, false)
  const showManage = useLocalStorage<boolean>(`showManage`, false)

  const shouldShowManage = computed(() => {
    return showManage.value && user.value?.is_super_admin
  })

  const toggleShowManage = () => {
    showManage.value = !showManage.value
  }

  const activateTabletMode = () => {
    useOverlay().create(ActivateTabletModeModal, {
      props: {
        onCancel: () => {
        },
        onConfirm: () => {
          // Logout wihtout redirect.
          const token = useCookie('token')
          authenticated.value = false
          user.value = null
          token.value = null

          // Set tablet mode.
          tabletMode.value = true

          // Go to the tablet page.
          navigateTo('/tablet')
        },
      },
    }).open()
  }

  const deactivateTabletMode = () => {
    tabletMode.value = false
    navigateTo('/')
  }

  const getUnreadNotificationsCount = computed(() => {
    return notifications.value.filter(notification => !notification.read_at).length
  })

  const hasUnreadNotifications = computed(() => {
    return notifications.value.some(notification => !notification.read_at)
  })

  function unimpersonate() {
    const impToken = useCookie('imp_token')
    impToken.value = null

    reloadNuxtApp()
  }

  function isImpersonating() {
    return useCookie('imp_token').value !== null && useCookie('imp_token').value !== undefined
  }

  function setImpersonateToken(token: string) {
    const impToken = useCookie('imp_token')
    impToken.value = token

    reloadNuxtApp()
  }

  async function loadFreshNotifications() {
    if (user.value) {
      const newNotifications = await $api('/notifications')
      notifications.value = newNotifications ?? []
    }
  }

  function setUser(newUser: User) {
    user.value = newUser
  }

  async function updateLocale(newLocale: 'en' | 'nl' | 'de' | 'fr') {
    if (user.value) {
      user.value.langcode = newLocale
      await $api('/user/update-locale', {
        method: 'POST',
        body: { langcode: newLocale },
      })
      await getUser(true)
    }
    nuxtApp.runWithContext(() => {
      $i18n.setLocale(newLocale as 'en' | 'nl' | 'de')
    })
  }

  async function getOngoingGame(force: boolean = false) {
    if (ongoingGame.value === null || force) {
      await $api('/user/ongoing-game').then((data) => {
        ongoingGame.value = data
      })
    }

    return ongoingGame
  }

  async function getUser(force: boolean = false) {
    if (user.value === null || force) {
      if (getToken()) {
        await nuxtApp.runWithContext(async () => {
          return await $api('/user')
        }).then((data) => {
          if (data === null) {
            throw new Error('Failed to login')
          }
          user.value = data
          notifications.value = data.notifications ?? []
          if (user.value?.langcode) {
            nuxtApp.runWithContext(() => {
              $i18n.setLocale(user.value?.langcode as 'en' | 'nl' | 'de' || 'en')
            })
          }
        }).catch((e) => {
          console.error('Failed to get user', e)
          logUserOut()
          user.value = null
        })
      }
      else {
        user.value = null
      }
    }

    return user.value
  }

  function setToken(token: string) {
    useCookie('token').value = token
    authenticated.value = true
    getUser()
  }

  async function authenticateUser(new_token: string) {
    const token = useCookie('token', { maxAge: 31536000 * 2 })
    if (new_token !== null) {
      token.value = new_token
      authenticated.value = true
      await getUser()
    }
  }

  function getToken() {
    return useCookie('token').value
  }

  function logUserOut() {
    try {
      const token = useCookie('token')
      authenticated.value = false
      user.value = null
      token.value = null
    }
    catch (e) {
      console.error(e)
    }

    if (window) {
      window.location.reload()
    }
  }

  return {
    authenticated,
    loading,
    user,
    showSidebar,
    hasUnreadNotifications,
    getUnreadNotificationsCount,
    didSaveNotifications,
    notifications,
    tabletMode,
    isPrinterConnected,
    unimpersonate,
    isImpersonating,
    setImpersonateToken,
    loadFreshNotifications,
    setUser,
    updateLocale,
    getUser,
    setToken,
    authenticateUser,
    getToken,
    getOngoingGame,
    logUserOut,
    activateTabletMode,
    deactivateTabletMode,
    shouldShowManage,
    toggleShowManage,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
